import {
  computePosition,
  flip,
  shift,
  offset,
  arrow,
  Placement,
} from '@floating-ui/dom';

export interface PopoverOptions {
  placement?: Placement;
  offset?: number;
  delay?: [number, number]
  trigger?: 'hover' | 'click' | 'focus';
  theme?: string;
  arrowSize?: number;
  maxWidth?: string;
}

export interface PopoverInstance {
  show: () => void;
  hide: () => void;
  destroy: () => void;
  isShown: () => boolean;
}

const DEFAULT_OPTIONS: PopoverOptions = {
  placement: 'bottom',
  offset: 8,
  delay: [0, 120],
  trigger: 'hover',
  theme: '',
  arrowSize: 8,
  maxWidth: '300px',
};

const resolveOffset = (options: PopoverOptions) => options.offset ?? DEFAULT_OPTIONS.offset ?? 8;
const resolveArrowSize = (options: PopoverOptions) => options.arrowSize ?? DEFAULT_OPTIONS.arrowSize ?? 8;
const resolveMaxWidth = (options: PopoverOptions) => options.maxWidth ?? DEFAULT_OPTIONS.maxWidth ?? '300px';

export function createPopover(
  trigger: HTMLElement,
  content: string | HTMLElement,
  options: PopoverOptions = {}
): PopoverInstance {
  const config = { ...DEFAULT_OPTIONS, ...options };

  const popoverEl = document.createElement('div');
  popoverEl.className = `popover ${config.theme ? `popover--${config.theme}` : ''}`;
  popoverEl.style.position = 'absolute';
  popoverEl.style.top = '0';
  popoverEl.style.left = '0';
  popoverEl.style.display = 'none';
  popoverEl.style.maxWidth = resolveMaxWidth(config);

  const arrowEl = document.createElement('div');
  arrowEl.className = 'popover-arrow';
  arrowEl.style.position = 'absolute';
  arrowEl.style.width = `${resolveArrowSize(config)}px`;
  arrowEl.style.height = `${resolveArrowSize(config)}px`;

  if (typeof content === 'string') {
    popoverEl.textContent = content;
  } else {
    popoverEl.appendChild(content);
  }

  popoverEl.appendChild(arrowEl);
  document.body.appendChild(popoverEl);

  let showTimeout: ReturnType<typeof setTimeout> | null = null;
  let hideTimeout: ReturnType<typeof setTimeout> | null = null;
  let isShown = false;
  let handleDocumentClick: ((event: MouseEvent) => void) | null = null;

  const triggerClick = (event: MouseEvent) => {
    event.stopPropagation();

    if (isShown) {
      hide();
    } else {
      show();
    }
  };

  const clearTimeouts = () => {
    if (showTimeout) clearTimeout(showTimeout);
    if (hideTimeout) clearTimeout(hideTimeout);
  };

  const updatePosition = async () => {
    const { x, y, placement, middlewareData } = await computePosition(
      trigger,
      popoverEl,
      {
        placement: config.placement,
        middleware: [
          offset(resolveOffset(config)),
          flip(),
          shift({ padding: 8 }),
          arrow({ element: arrowEl, padding: 4 }),
        ],
      }
    );

    popoverEl.style.left = `${x}px`;
    popoverEl.style.top = `${y}px`;

    const arrowData = middlewareData.arrow;
    if (arrowData) {
      const side = placement.split('-')[0];
      const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
      }[side] as string;

      Object.assign(arrowEl.style, {
        left: arrowData.x != null ? `${arrowData.x}px` : '',
        top: arrowData.y != null ? `${arrowData.y}px` : '',
        [staticSide]: `-${resolveArrowSize(config) / 2}px`,
      });
    }
  };

  const show = async () => {
    clearTimeouts();
    isShown = true;
    popoverEl.style.display = 'block';
    await updatePosition();
  };

  const hide = () => {
    clearTimeouts();
    isShown = false;
    popoverEl.style.display = 'none';
  };

  const scheduleShow = () => {
    clearTimeouts();
    const [delay] = config.delay!;
    if (delay > 0) {
      showTimeout = setTimeout(show, delay);
    } else {
      show();
    }
  };

  const scheduleHide = () => {
    clearTimeouts();
    const [, delay] = config.delay!;
    if (delay > 0) {
      hideTimeout = setTimeout(hide, delay);
    } else {
      hide();
    }
  };

  const handleScroll = () => {
    if (isShown) {
      updatePosition();
    }
  };

  if (config.trigger === 'hover') {
    trigger.addEventListener('mouseenter', scheduleShow);
    trigger.addEventListener('mouseleave', scheduleHide);
    popoverEl.addEventListener('mouseenter', scheduleShow);
    popoverEl.addEventListener('mouseleave', scheduleHide);
  } else if (config.trigger === 'click') {
    trigger.addEventListener('click', triggerClick);

    handleDocumentClick = (e: MouseEvent) => {
      if (!popoverEl.contains(e.target as Node) && !trigger.contains(e.target as Node)) {
        hide();
      }
    };

    document.addEventListener('click', handleDocumentClick);
  } else if (config.trigger === 'focus') {
    trigger.addEventListener('focus', scheduleShow);
    trigger.addEventListener('blur', scheduleHide);
  }

  window.addEventListener('scroll', handleScroll, true);

  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isShown) {
      hide();
    }
  };
  document.addEventListener('keydown', handleKeydown);

  const destroy = () => {
    clearTimeouts();
    popoverEl.remove();

    if (config.trigger === 'hover') {
      trigger.removeEventListener('mouseenter', scheduleShow);
      trigger.removeEventListener('mouseleave', scheduleHide);
      popoverEl.removeEventListener('mouseenter', scheduleShow);
      popoverEl.removeEventListener('mouseleave', scheduleHide);
    } else if (config.trigger === 'click') {
      trigger.removeEventListener('click', triggerClick);
      if (handleDocumentClick) {
        document.removeEventListener('click', handleDocumentClick);
      }
    } else if (config.trigger === 'focus') {
      trigger.removeEventListener('focus', scheduleShow);
      trigger.removeEventListener('blur', scheduleHide);
    }

    window.removeEventListener('scroll', handleScroll, true);
    document.removeEventListener('keydown', handleKeydown);
  };

  return {
    show,
    hide,
    destroy,
    isShown: () => isShown,
  };
}
